<template>
  <div class="checkout-container">
    <!-- Sol Panel: Ödeme Yöntemleri -->
    <div class="payment-panel" v-if="!paymentSuccess">
      <img
        src="@/assets/img/logo.png"
        alt="Nextline Logo"
        class="payment-logo"
      />

      <!-- Mobile Order Summary Container -->
      <div class="mobile-order-summary-container">
        <!-- Light Overlay -->
        <div
          class="light-overlay"
          v-if="orderSummaryExpanded"
          @click="toggleOrderSummary"
        ></div>

        <!-- Order Summary Header -->
        <div
          class="mobile-order-summary-header"
          v-if="hasPackageId"
          @click="toggleOrderSummary"
        >
          <div class="order-summary-left">
            <span class="order-summary-title">{{
              $t("checkout.order_summary")
            }}</span>
            <div
              class="collapse-icon"
              :class="{ expanded: orderSummaryExpanded }"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M4 6L8 10L12 6"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
          </div>
          <div class="order-summary-price">
            {{ productDetail?.price_text }}
          </div>
        </div>

        <!-- Order Summary Details (Dropdown) -->
        <div class="mobile-order-summary-details" v-if="orderSummaryExpanded">
          <!-- Ürün Bilgileri -->
          <div class="product-card">
            <div class="product-header">
              <div class="country-info">
                <div class="flag-container">
                  <img
                    :src="getCountryFlag(productDetail?.icon_key)"
                    alt="Flag"
                    class="country-flag"
                    @load="onFlagLoad"
                    @error="onFlagError"
                    v-show="!flagLoading"
                  />
                  <div class="flag-loading" v-if="flagLoading">
                    <div class="flag-spinner"></div>
                  </div>
                </div>
                <span class="country-name">{{ productDetail?.title }}</span>
              </div>
              <div class="product-price">{{ productDetail?.price_text }}</div>
            </div>

            <div class="product-details">
              <div class="detail-item">
                <span class="detail-label">{{
                  $t("checkout.coverage_area")
                }}</span>
                <span class="detail-value">{{
                  productDetail?.coverage_text
                }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">{{ $t("checkout.data") }}</span>
                <span class="detail-value">{{
                  productDetail?.data_volume_text
                }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">{{
                  $t("checkout.validity_period")
                }}</span>
                <span class="detail-value">{{
                  productDetail?.duration_text
                }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">{{ $t("checkout.price") }}</span>
                <span class="detail-value">{{
                  productDetail?.price_text
                }}</span>
              </div>
            </div>
          </div>

          <!-- Network Bilgisi -->
          <div class="info-card" v-if="getAdditionalInfo('network')">
            <h4 class="info-title">{{ $t("checkout.network") }}</h4>
            <p class="info-description">
              {{ getAdditionalInfo("network")?.description }}
            </p>
          </div>

          <!-- Additional Information -->
          <div class="info-card">
            <h4 class="info-title">
              {{ $t("checkout.additional_information") }}
            </h4>
            <div class="additional-details">
              <div class="detail-item" v-if="getAdditionalInfo('plan_type')">
                <span class="detail-label"
                  >{{ getAdditionalInfo("plan_type")?.title }}:</span
                >
                <span class="detail-value">{{
                  getAdditionalInfo("plan_type")?.description
                }}</span>
              </div>
              <div class="detail-item" v-if="getAdditionalInfo('apn')">
                <span class="detail-label"
                  >{{ getAdditionalInfo("apn")?.title }}:</span
                >
                <span class="detail-value">{{
                  getAdditionalInfo("apn")?.description
                }}</span>
              </div>
              <div class="detail-item" v-if="getAdditionalInfo('roaming')">
                <span class="detail-label">{{
                  $t("checkout.international_circulation")
                }}</span>
                <span class="detail-value">{{
                  getAdditionalInfo("roaming")?.description
                }}</span>
              </div>
            </div>
          </div>

          <!-- Total -->
          <div class="total-section">
            <div class="total-row">
              <span class="total-label">{{ $t("checkout.total") }}</span>
              <span class="total-price">
                {{ productDetail?.price_text }}
              </span>
            </div>
          </div>

          <!-- Footer -->
          <div class="order-footer">
            <p class="footer-text">
              {{ $t("checkout.made_with_love") }}
            </p>
          </div>
        </div>
      </div>
      <!-- End Mobile Order Summary Container -->

      <h1 class="payment-title">{{ $t("checkout.choose_payment_method") }}</h1>

      <!-- User Information Form -->
      <div
        class="user-info-card"
        v-if="!showStripeForm && shouldShowEmailField"
      >
        <h3 class="user-info-title">{{ $t("checkout.user_information") }}</h3>

        <!-- User Information Section -->
        <div class="user-details-section">
          <div class="form-group">
            <input
              type="email"
              v-model="userInfo.email"
              :placeholder="$t('checkout.email_required')"
              class="form-input"
              :class="{ error: errors.email }"
              @input="clearError('email')"
            />
            <div class="field-error" v-if="errors.email">
              {{ errors.email }}
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <input
                type="text"
                v-model="userInfo.firstName"
                :placeholder="$t('checkout.first_name_optional')"
                class="form-input"
                :class="{ error: errors.firstName }"
                @input="clearError('firstName')"
              />
              <div class="field-error" v-if="errors.firstName">
                {{ errors.firstName }}
              </div>
            </div>
            <div class="form-group">
              <input
                type="text"
                v-model="userInfo.lastName"
                :placeholder="$t('checkout.last_name_optional')"
                class="form-input"
                :class="{ error: errors.lastName }"
                @input="clearError('lastName')"
              />
              <div class="field-error" v-if="errors.lastName">
                {{ errors.lastName }}
              </div>
            </div>
          </div>

          <div class="form-group">
            <input
              type="tel"
              v-model="userInfo.phone"
              :placeholder="$t('checkout.phone_optional')"
              class="form-input phone-input"
              :class="{ error: errors.phone }"
              @input="handlePhoneInput"
              @keypress="allowOnlyNumbers"
            />
            <div class="field-error" v-if="errors.phone">
              {{ errors.phone }}
            </div>
          </div>
        </div>

        <!-- Şartlar ve Koşullar -->
        <p class="terms-text">
          {{ $t("checkout.agree_terms_part1") }}
          <a
            :href="getBaseUrl() + '/terms-of-service'"
            class="terms-link"
            target="_blank"
            >{{ $t("checkout.terms_conditions") }}</a
          >
          {{ $t("checkout.and") }}
          <a
            :href="getBaseUrl() + '/privacy-policy'"
            class="terms-link"
            target="_blank"
            >{{ $t("checkout.privacy_policy") }}</a
          >{{ $t("checkout.agree_terms_part2") }}
        </p>

        <!-- Devam Et Butonu -->
        <button
          class="continue-button"
          @click="submitUserInfo"
          :disabled="loading"
        >
          {{ $t("checkout.continue_with_payment") }}
        </button>
      </div>

      <!-- Express Checkout - Payment settings tamamlandıktan sonra göster -->
      <!-- Currency "try" ise PayPal gözükmeyecek -->
      <div
        class="express-checkout-card"
        v-if="
          (!shouldShowEmailField || showStripeForm) &&
          productInfo &&
          productInfo.currency?.toLowerCase() !== 'try'
        "
      >
        <h3 class="express-checkout-title">
          {{ $t("checkout.express_checkout") }}
        </h3>
        <button
          class="paypal-button"
          @click="paypalPayButtonClicked()"
          :disabled="loading"
        >
          <img src="@/assets/img/payment/paypal-icon.svg" alt="PayPal" />
        </button>
      </div>

      <!-- OR Separator - Payment settings tamamlandıktan sonra göster -->
      <!-- Currency "try" ise OR separator gözükmeyecek -->
      <div
        class="or-separator"
        v-if="
          (!shouldShowEmailField || showStripeForm) &&
          productInfo &&
          productInfo.currency?.toLowerCase() !== 'try'
        "
      >
        <div class="or-line"></div>
        <span class="or-text">{{ $t("checkout.or") }}</span>
        <div class="or-line"></div>
      </div>

      <!-- Stripe Form (Gizli) -->
      <div class="stripe-form-container" v-if="showStripeForm">
        <div class="stripe-form-header">
          <h3 class="stripe-form-title">
            {{ $t("checkout.pay_with_credit_card") }}
          </h3>
        </div>

        <!-- Credit Card Header -->
        <div class="credit-card-header">
          <span class="credit-card-label">{{
            $t("checkout.credit_card")
          }}</span>
          <div class="credit-card-icon-container">
            <img src="@/assets/img/creditCard.svg" alt="credit card" />
          </div>
        </div>

        <!-- Inner Credit Card Container -->
        <div class="credit-card-inner">
          <!-- Stripe Elements Form -->
          <SrCheckoutForm @payment-success="handlePaymentSuccess" />
        </div>

        <div class="discount-section discount-section-app">
          <div class="discount-input-group">
            <input
              type="text"
              :placeholder="$t('checkout.discount_code')"
              class="discount-input"
              v-model="discountCode"
              :disabled="!orderId"
            />
            <button
              class="apply-button"
              :class="{ 'has-discount': discountCode && discountCode.trim() }"
              @click="applyDiscount"
              :disabled="!orderId || !discountCode || !discountCode.trim()"
            >
              {{ $t("checkout.apply") }}
            </button>
          </div>
        </div>

        <p class="terms-text">
          {{ $t("checkout.agree_terms_part1") }}
          <a
            :href="getBaseUrl() + '/terms-of-service'"
            class="terms-link"
            target="_blank"
            >{{ $t("checkout.terms_conditions") }}</a
          >
          {{ $t("checkout.and") }}
          <a
            :href="getBaseUrl() + '/privacy-policy'"
            class="terms-link"
            target="_blank"
            >{{ $t("checkout.privacy_policy") }}</a
          >{{ $t("checkout.agree_terms_part2") }}
        </p>

        <!-- Pay Now Button -->
        <button
          class="pay-now-button"
          @click="triggerStripePayment"
          :disabled="loading"
        >
          {{ $t("checkout.pay_now") }}
        </button>
      </div>

      <!-- Footer -->
      <div class="payment-footer">
        <p class="rights-text">{{ $t("checkout.all_rights_reserved") }}</p>
        <div class="secure-payment-logos">
          <img src="@/assets/img/secure.svg" alt="Secure" class="secure-logo" />
          <img
            src="@/assets/img/secureVisa.svg"
            alt="Visa Secure"
            class="visa-logo"
          />
          <img
            src="@/assets/img/sucureMaster.svg"
            alt="Mastercard Secure"
            class="master-logo"
          />
          <img
            src="@/assets/img/sucreGoogle.svg"
            alt="Google Pay Secure"
            class="google-logo"
          />
        </div>
      </div>
    </div>

    <!-- Success Panel -->
    <div class="success-panel" v-if="paymentSuccess">
      <!-- Logo -->
      <img
        src="@/assets/img/logo.png"
        alt="Nextline Logo"
        class="payment-logo"
      />

      <!-- Mobile Order Summary Container (Success) -->
      <div class="mobile-order-summary-container">
        <!-- Light Overlay -->
        <div
          class="light-overlay"
          v-if="orderSummaryExpanded"
          @click="toggleOrderSummary"
        ></div>

        <!-- Order Summary Header -->
        <div
          class="mobile-order-summary-header"
          v-if="hasPackageId"
          @click="toggleOrderSummary"
        >
          <div class="order-summary-left">
            <span class="order-summary-title">{{
              $t("checkout.order_summary")
            }}</span>
            <div
              class="collapse-icon"
              :class="{ expanded: orderSummaryExpanded }"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M4 6L8 10L12 6"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
          </div>
          <div class="order-summary-price">
            {{ productDetail?.price_text }}
          </div>
        </div>

        <!-- Order Summary Details (Dropdown) -->
        <div class="mobile-order-summary-details" v-if="orderSummaryExpanded">
          <!-- Ürün Bilgileri -->
          <div class="product-card">
            <div class="product-header">
              <div class="country-info">
                <div class="flag-container">
                  <img
                    :src="getCountryFlag(productDetail?.icon_key)"
                    alt="Flag"
                    class="country-flag"
                    @load="onFlagLoad"
                    @error="onFlagError"
                    v-show="!flagLoading"
                  />
                  <div class="flag-loading" v-if="flagLoading">
                    <div class="flag-spinner"></div>
                  </div>
                </div>
                <span class="country-name">{{ productDetail?.title }}</span>
              </div>
              <div class="product-price">{{ productDetail?.price_text }}</div>
            </div>

            <div class="product-details">
              <div class="detail-item">
                <span class="detail-label">{{
                  $t("checkout.coverage_area")
                }}</span>
                <span class="detail-value">{{
                  productDetail?.coverage_text
                }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">{{ $t("checkout.data") }}</span>
                <span class="detail-value">{{
                  productDetail?.data_volume_text
                }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">{{
                  $t("checkout.validity_period")
                }}</span>
                <span class="detail-value">{{
                  productDetail?.duration_text
                }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">{{ $t("checkout.price") }}</span>
                <span class="detail-value">{{
                  productDetail?.price_text
                }}</span>
              </div>
            </div>
          </div>

          <!-- Network Bilgisi -->
          <div class="info-card" v-if="getAdditionalInfo('network')">
            <h4 class="info-title">{{ $t("checkout.network") }}</h4>
            <p class="info-description">
              {{ getAdditionalInfo("network")?.description }}
            </p>
          </div>

          <!-- Additional Information -->
          <div class="info-card">
            <h4 class="info-title">
              {{ $t("checkout.additional_information") }}
            </h4>
            <div class="additional-details">
              <div class="detail-item" v-if="getAdditionalInfo('plan_type')">
                <span class="detail-label"
                  >{{ getAdditionalInfo("plan_type")?.title }}:</span
                >
                <span class="detail-value">{{
                  getAdditionalInfo("plan_type")?.description
                }}</span>
              </div>
              <div class="detail-item" v-if="getAdditionalInfo('apn')">
                <span class="detail-label"
                  >{{ getAdditionalInfo("apn")?.title }}:</span
                >
                <span class="detail-value">{{
                  getAdditionalInfo("apn")?.description
                }}</span>
              </div>
              <div class="detail-item" v-if="getAdditionalInfo('roaming')">
                <span class="detail-label">{{
                  $t("checkout.international_circulation")
                }}</span>
                <span class="detail-value">{{
                  getAdditionalInfo("roaming")?.description
                }}</span>
              </div>
            </div>
          </div>

          <!-- Total -->
          <div class="total-section">
            <div class="total-row">
              <span class="total-label">{{ $t("checkout.total") }}</span>
              <span class="total-price">
                {{ productDetail?.price_text }}
              </span>
            </div>
          </div>

          <!-- Footer -->
          <div class="order-footer">
            <p class="footer-text">
              {{ $t("checkout.made_with_love") }}
            </p>
          </div>
        </div>
      </div>
      <!-- End Mobile Order Summary Container -->

      <!-- Thank You Section -->
      <div class="thank-you-section">
        <div class="success-icon">
          <img
            src="@/assets/img/Ok.svg"
            alt="Success"
            class="success-checkmark"
          />
        </div>
        <div class="thank-you-content">
          <div class="confirmation-id">{{ $t("checkout.confirmation") }}</div>
          <div class="thank-you-text">{{ $t("checkout.thank_you") }}</div>
        </div>
      </div>

      <!-- Main Content Card -->
      <div class="success-main-card">
        <!-- Order Confirmed Card -->
        <div class="order-confirmed-card">
          <div class="order-confirmed-content">
            <h1 class="order-confirmed-title">
              {{ $t("checkout.order_confirmed") }}
            </h1>
            <p class="order-confirmed-description">
              {{ $t("checkout.confirmation_email") }}
            </p>
          </div>
        </div>

        <!-- Order Details Card -->
        <div class="order-details-card">
          <h3 class="order-details-title">
            {{ $t("checkout.order_details") }}
          </h3>
          <div class="payment-method-info">
            <span class="payment-method-label">{{
              $t("checkout.payment_method")
            }}</span>
            <div class="payment-method-details">
              <img
                src="@/assets/img/visa-white.svg"
                alt="Visa"
                class="card-brand-icon"
              />
              <span class="card-number">**** </span>
              <span class="currency">{{ productDetail?.price_text }}</span>
            </div>
          </div>
        </div>

        <!-- Action Section -->
        <div class="success-actions">
          <div class="help-section">
            <span class="help-text">{{ $t("checkout.need_help") }} </span>
            <a
              :href="getBaseUrl() + '/help-center#contact-us'"
              class="contact-link"
              target="_blank"
              >{{ $t("checkout.contact_us") }}</a
            >
          </div>
          <button class="continue-shopping-btn" @click="continueShopping">
            {{ $t("checkout.continue_shopping") }}
          </button>
        </div>
      </div>

      <!-- Footer -->
      <div class="payment-footer">
        <p class="rights-text">{{ $t("checkout.all_rights_reserved") }}</p>
        <div class="secure-payment-logos">
          <img src="@/assets/img/secure.svg" alt="Secure" class="secure-logo" />
          <img
            src="@/assets/img/secureVisa.svg"
            alt="Visa Secure"
            class="visa-logo"
          />
          <img
            src="@/assets/img/sucureMaster.svg"
            alt="Mastercard Secure"
            class="master-logo"
          />
          <img
            src="@/assets/img/sucreGoogle.svg"
            alt="Google Pay Secure"
            class="google-logo"
          />
        </div>
      </div>
    </div>

    <!-- Sağ Panel: Sipariş Detayları -->
    <div class="order-panel">
      <!-- Ürün Bilgileri -->
      <div class="product-card">
        <div class="product-header">
          <div class="country-info">
            <img
              :src="getCountryFlag(productDetail?.icon_key)"
              alt="Flag"
              class="country-flag"
            />
            <span class="country-name">{{ productDetail?.title }}</span>
          </div>
          <div class="product-price">{{ productDetail?.price_text }}</div>
        </div>

        <div class="product-details">
          <div class="detail-item">
            <span class="detail-label">{{ $t("checkout.coverage_area") }}</span>
            <span class="detail-value">{{ productDetail?.coverage_text }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">{{ $t("checkout.data") }}</span>
            <span class="detail-value">{{
              productDetail?.data_volume_text
            }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">{{
              $t("checkout.validity_period")
            }}</span>
            <span class="detail-value">{{ productDetail?.duration_text }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">{{ $t("checkout.price") }}</span>
            <span class="detail-value">{{ productDetail?.price_text }}</span>
          </div>
        </div>
      </div>

      <!-- Network Bilgisi -->
      <div class="info-card" v-if="getAdditionalInfo('network')">
        <h4 class="info-title">{{ $t("checkout.network") }}</h4>
        <p class="info-description">
          {{ getAdditionalInfo("network")?.description }}
        </p>
      </div>

      <!-- Additional Information -->
      <div class="info-card">
        <h4 class="info-title">{{ $t("checkout.additional_information") }}</h4>
        <div class="additional-details">
          <div class="detail-item" v-if="getAdditionalInfo('plan_type')">
            <span class="detail-label"
              >{{ getAdditionalInfo("plan_type")?.title }}:</span
            >
            <span class="detail-value">{{
              getAdditionalInfo("plan_type")?.description
            }}</span>
          </div>
          <div class="detail-item" v-if="getAdditionalInfo('apn')">
            <span class="detail-label"
              >{{ getAdditionalInfo("apn")?.title }}:</span
            >
            <span class="detail-value">{{
              getAdditionalInfo("apn")?.description
            }}</span>
          </div>
          <div class="detail-item" v-if="getAdditionalInfo('roaming')">
            <span class="detail-label">{{
              $t("checkout.international_circulation")
            }}</span>
            <span class="detail-value">{{
              getAdditionalInfo("roaming")?.description
            }}</span>
          </div>
        </div>
      </div>

      <!-- Discount Code -->
      <div class="discount-section">
        <div class="discount-input-group">
          <input
            type="text"
            :placeholder="$t('checkout.discount_code')"
            class="discount-input"
            v-model="discountCode"
            :disabled="!orderId"
          />
          <button
            class="apply-button"
            :class="{ 'has-discount': discountCode && discountCode.trim() }"
            @click="applyDiscount"
            :disabled="!orderId || !discountCode || !discountCode.trim()"
          >
            {{ $t("checkout.apply") }}
          </button>
        </div>
      </div>

      <!-- Total -->
      <div class="total-section">
        <div class="total-row">
          <span class="total-label">{{ $t("checkout.total") }}</span>
          <span class="total-price">
            {{ productDetail?.price_text }}
          </span>
        </div>
      </div>

      <!-- Footer -->
      <div class="order-footer">
        <p class="footer-text">
          {{ $t("checkout.made_with_love") }}
        </p>
      </div>
    </div>

    <!-- Loading Overlay -->
    <v-overlay color="#fff" :value="showFullPageLoading">
      <v-progress-circular
        color="#333"
        :size="50"
        indeterminate
      ></v-progress-circular>
    </v-overlay>
  </div>
</template>
<style lang="scss">
@import "@/components/Payment/Checkout.scss";
</style>
<script>
"use strict";

import { mapState, mapActions } from "vuex";
import { PaymentProvider } from "@/helper/enums";
import { Logger } from "@/logging";
import { generatePaymentUUID, getPaymentUUID } from "@/helper/index.js";
import { createStripePaymentData, stripeUuid } from "@/helper/index.js";

import { check_hybridapi_method_exist } from "@/hybridapi";

import PayPal from "@/paypal";

import SrCheckoutForm from "./Stripe/SrCheckoutForm.vue";

const logger = new Logger("PaymentCheckout");
const endpoints = require("@/api/endpoints");

import { get_redirect_url_map, get_redirect_urls } from "@/helper/redirectUrls";

export default {
  name: "CheckoutComponent",
  components: {
    SrCheckoutForm,
  },
  data() {
    return {
      paymentMethod: {
        payment_provider: PaymentProvider.PAYPAL,
      },
      stripePaymentMethods: null,
      loading: false,
      cardError: null,
      showFullPageLoading: false,
      showStripePaymentMethods: false,
      showCreditCardPayment: false,
      showCheckoutPage: true,
      showSaveCardPermission: true,
      isSaveCard: true,
      paypalPaymentTokens: null,
      savedPaymentTokenId: null,
      width: "0px",
      showStripeRemove: false,
      supportedCardIcons: ["amex", "mastercard", "visa"],
      show_stripe: true,
      show_paypal: true,
      orderId: null,
      productDetail: null,
      productDetailLoading: false,
      productInfo: null, // payment_methods API'den gelen product_info
      discountCode: "",
      paymentSuccess: false,
      orderSummaryExpanded: false,
      paymentData: null,
      flagLoading: true,
      // Yeni kullanıcı bilgileri formu için
      showStripeForm: false,
      userInfo: {
        email: "",
        firstName: "",
        lastName: "",
        phone: "",
      },
      errors: {},
    };
  },
  computed: {
    ...mapState({
      selectedProduct: (state) => state.payment.selectedProduct,
      selectedPaymentMethod: (state) => state.payment.selectedPaymentMethod,
      currencies: (state) => state.client.currencies,
      ownProfile: (state) => state.client.ownProfile,
      deviceId: (state) => state.client.deviceId,
    }),
    currentLocale() {
      // Vue i18n'den mevcut locale'i al
      return this.$i18n.locale || "en";
    },
    isWebPlatform() {
      // URL'de platform=web varsa web, yoksa app
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get("platform") === "web";
    },
    userEmailFromUrl() {
      // Önce URL'den user_email parametresini al
      const urlParams = new URLSearchParams(window.location.search);
      let email = urlParams.get("user_email");

      // URL'de yoksa sessionStorage'dan (erken yakalanan) parametreleri dene
      if (!email) {
        try {
          const saved = sessionStorage.getItem("stripe_redirect_params");
          if (saved) {
            const parsed = JSON.parse(saved);
            if (parsed && typeof parsed.user_email === "string") {
              email = parsed.user_email;
            }
          }
        } catch (e) {
          // ignore
        }
      }

      return email;
    },
    shouldShowEmailField() {
      // App platformunda form hiç gözükmesin
      if (!this.isWebPlatform) {
        return false;
      }
      // Web platformunda: user_email varsa email field'ını gösterme
      return !this.userEmailFromUrl;
    },
    hasPackageId() {
      // Route'dan package_id ve product_id kontrolü
      let package_id =
        this.$route.query.package_id || this.$route.params.package_id;
      let product_id = this.$route.query.product_id;

      // URL'de yoksa sessionStorage'dan dene (Safari gizli sekme için)
      if (!package_id || !product_id) {
        try {
          const savedParams = sessionStorage.getItem("stripe_redirect_params");
          if (savedParams) {
            const parsed = JSON.parse(savedParams);
            if (!package_id && parsed && parsed.package_id) {
              package_id = parsed.package_id;
            }
            if (!product_id && parsed && parsed.product_id) {
              product_id = parsed.product_id;
            }
          }
        } catch (e) {
          // ignore
        }
      }

      // package_id veya product_id varsa true döndür
      return !!(package_id || product_id);
    },
  },
  beforeMount() {
    // URL'den locale parametresini kontrol et ve i18n'i güncelle (EN ÖNCE)
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const urlLocale = urlParams.get("locale");
      const supportedLocales = [
        "en",
        "tr",
        "es",
        "fr",
        "de",
        "it",
        "pt",
        "ru",
        "ja",
        "zh",
        "ar",
        "th",
      ];

      if (urlLocale && supportedLocales.includes(urlLocale)) {
        // i18n locale'ini güncelle
        this.$i18n.locale = urlLocale;
        localStorage.setItem("locale", urlLocale);
        console.log("Locale set to (beforeMount):", urlLocale);
      }
    } catch (e) {
      console.warn("Locale setting error:", e);
    }
  },
  mounted() {
    window["backButtonTapped"] = () => {
      if (confirm(this.$t("labels.close_confirm"))) {
        this.$hybridapi("close", "close");
        return "close";
      }
      return "nothing";
    };

    this.$hybridapi("updateWindowSize", "medium");
    this.$hybridapi("showLoading");
    this.$hybridapi("updatePageTitle", this.$t("labels.do_payment"));

    this.onDocumentResize();
    window.addEventListener("resize", this.onDocumentResize, true);

    // Tamamlanmış ödeme akışından gelindiyse paymentSuccess'i set et
    try {
      const earlyParams = new URLSearchParams(window.location.search);
      const isCompletedFlow =
        earlyParams.get("payment_success") === "true" ||
        earlyParams.get("redirect_status") === "succeeded";
      if (isCompletedFlow) {
        this.paymentSuccess = true;
        // Success durumunda sadece product detail yükle, ödeme isteklerini atma
        let package_id =
          this.$route.query.package_id || this.$route.params.package_id;
        let product_id = this.$route.query.product_id;

        // URL'de yoksa sessionStorage'dan al (Safari gizli sekme için)
        if (!package_id) {
          try {
            const savedParams = sessionStorage.getItem(
              "stripe_redirect_params"
            );
            if (savedParams) {
              const parsed = JSON.parse(savedParams);
              if (parsed && parsed.package_id) {
                package_id = parsed.package_id;
              }
              if (!product_id && parsed && parsed.product_id) {
                product_id = parsed.product_id;
              }
            }
          } catch (e) {
            // ignore
          }
        }

        if (package_id) {
          this.getProductDetail(package_id, null).catch((err) => {
            // Product detail yüklenemediğinde loading'i kapat
            this.loading = false;
            this.$hybridapi("hideLoading");
            console.error(
              "getProductDetail error in mounted (paymentSuccess):",
              err
            );
          });
        } else if (product_id) {
          this.getProductDetail(null, product_id).catch((err) => {
            // Product detail yüklenemediğinde loading'i kapat
            this.loading = false;
            this.$hybridapi("hideLoading");
            console.error(
              "getProductDetail error in mounted (paymentSuccess):",
              err
            );
          });
        }
      } else {
        this.initialize();
      }
    } catch (e) {
      this.initialize();
    }

    generatePaymentUUID();
    this.getSavedPaymentToken();

    // Web platformunda: URL'den user_email varsa payment_settings çağırıp Stripe formuna geç
    // App platformunda: user_email olsa bile form gösterilmemeli
    if (this.isWebPlatform && this.userEmailFromUrl && !this.paymentSuccess) {
      let product_id = this.$route.query.product_id;
      let product_type = this.$route.query.product_type
        ? parseInt(this.$route.query.product_type)
        : 0;

      if (product_id && product_type) {
        // Payment settings'i çağır, sonra Stripe formuna geç
        this.getPaymentMethods(product_id, product_type)
          .then(() => {
            this.showStripeForm = true;
          })
          .catch(() => {
            // Hata durumunda da formu göster
            this.showStripeForm = true;
          });
      } else {
        // product_id yoksa direkt formu aç
        this.showStripeForm = true;
      }
    }

    // URL parametresini kontrol et (gecikmeli durumlar için)
    this.checkPaymentSuccess();

    // İlk sayfa açıldığında session'ı kaydet (yeni sistem)
    this.saveSessionOnMount();
  },
  methods: {
    ...mapActions("payment", [
      "setSelectedPaymentMethod",
      "setHybridPaymentFlowInformationProps",
    ]),
    initialize() {
      if (this.paymentSuccess) {
        return;
      }
      let product_id = this.$route.query.product_id;
      let package_id =
        this.$route.query.package_id || this.$route.params.package_id;
      let product_type = this.$route.query.product_type
        ? parseInt(this.$route.query.product_type)
        : 0;

      // Package ID varsa ürün detayını getir, yoksa product_id ile dene
      console.log("Package ID from URL:", package_id);
      console.log("Product ID from URL:", product_id);
      if (package_id) {
        this.getProductDetail(package_id, null).catch((err) => {
          // Product detail yüklenemediğinde loading'i kapat
          this.loading = false;
          this.$hybridapi("hideLoading");
          console.error("getProductDetail error in initialize:", err);
        });
      } else if (product_id) {
        this.getProductDetail(null, product_id).catch((err) => {
          // Product detail yüklenemediğinde loading'i kapat
          this.loading = false;
          this.$hybridapi("hideLoading");
          console.error("getProductDetail error in initialize:", err);
        });
      } else {
        console.log("No package_id or product_id found");
        // package_id ve product_id yoksa loading'i kapat
        this.loading = false;
        this.$hybridapi("hideLoading");
      }

      // Web platformunda: Email varsa payment_settings'i çağır, yoksa sadece product detail göster
      // App platformunda: Email olmadan da payment_settings'i çağır
      if (
        product_id &&
        product_type &&
        (this.isWebPlatform ? this.userEmailFromUrl : true)
      ) {
        this.getPaymentMethods(product_id, product_type)
          .then(() => {
            // App modunda payment_methods çağrıldıktan sonra direkt Stripe formuna geç
            if (!this.isWebPlatform) {
              this.showStripeForm = true;
            }
          })
          .catch((err) => {
            // Hata durumunda loading'i kapat
            this.loading = false;
            this.$hybridapi("hideLoading");
            console.error("getPaymentMethods error in initialize:", err);
          });
      } else if (!this.isWebPlatform && package_id) {
        // App modunda sadece package_id varsa (product_id yoksa) direkt Stripe formunu göster
        this.showStripeForm = true;
      } else if (!package_id && !product_id) {
        this.$hybridapi("close", "close");
      }
    },
    getPaymentMethods(product_id, product_type) {
      if (this.paymentSuccess) {
        return Promise.resolve();
      }

      // Email bilgisini hazırla
      // App modunda: email key'i hiç gönderilmez
      // Web modunda: URL'den gelen user_email veya form'dan girilen email kullanılır
      const email = this.isWebPlatform
        ? this.userEmailFromUrl || this.userInfo.email
        : null;

      const requestData = {
        product_id: product_id,
        product_type: product_type,
        language: this.currentLocale,
      };

      // Email varsa ekle
      if (email) {
        requestData.email = email;
      }

      return endpoints
        .payment_methods(this.$axios, requestData)
        .then((paymentMethodResults) => {
          if (
            paymentMethodResults.data &&
            paymentMethodResults.data.payment_methods
          ) {
            this.stripePaymentMethods =
              paymentMethodResults.data.payment_methods.length > 0
                ? paymentMethodResults.data.payment_methods
                : null;
            this.orderId = paymentMethodResults.data.product_order_id;

            // product_info'yu sakla
            this.productInfo = paymentMethodResults.data.product_info;

            this.$store.dispatch(
              "payment/setSelectedProduct",
              paymentMethodResults.data.product_info
            );

            this.$hybridapi("hideLoading");
            if (
              this.stripePaymentMethods &&
              this.stripePaymentMethods.length > 0
            ) {
              this.showCreditCardPayment = false;
              this.showStripePaymentMethods = true;
              //this.setSelectedPaymentMethod(this.stripePaymentMethods[0]);
            } else {
              this.showCreditCardPayment = true;
              this.showStripePaymentMethods = false;
            }
          } else {
            console.log(paymentMethodResults.data);
            this.loading = false;
            this.$hybridapi("hideLoading");
            this.cardError = this.$t("errors.general_error");
          }
        })
        .catch((err) => {
          console.log(err);
          this.loading = false;
          this.$hybridapi("hideLoading");
          this.cardError = this.$t("errors.general_error");
          throw err;
        });
    },
    deletePaypalToken(savedPaymentToken) {
      this.loading = true;

      this.paypalPaymentTokens = this.paypalPaymentTokens.filter(
        (pt) => pt.id != savedPaymentToken.id
      );

      const payload = {
        payment_token_id: savedPaymentToken.id,
        provider: PaymentProvider.PAYPAL,
        language: this.currentLocale,
      };

      endpoints
        .delete_saved_card(this.$axios, payload)
        .then((deleteSavedCardResponse) => {
          const data = deleteSavedCardResponse.data;

          if (
            this.savedPaymentTokenId &&
            this.savedPaymentTokenId == savedPaymentToken.id
          ) {
            this.selectPaymentToken(null);
          }
          this.loading = false;
        })
        .catch((response) => {
          this.loading = false;
          logger.debugError(
            "Unexpected delete saved card exception caught",
            payload,
            response
          );
          this.$toast.error(this.$t("errors.general_error"));
          this.hybridClose("close");
        });
    },
    deleteStripeCard(paymentMethod) {
      this.loading = true;
      const paymentMethodId = paymentMethod.id;
      endpoints
        .detach_payment_method(this.$axios, {
          payment_method_id: paymentMethodId,
          language: this.currentLocale,
        })
        .then(() => {
          this.stripePaymentMethods = this.stripePaymentMethods.filter(
            (item) => item.id !== paymentMethodId
          );

          if (this.stripePaymentMethods.length > 0) {
            if (paymentMethodId === this.selectedPaymentMethod.id) {
              this.$store.dispatch(
                "payment/setSelectedPaymentMethod",
                this.stripePaymentMethods[0]
              );
            }
          }
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    proceedWithStripe() {
      if (this.paymentSuccess) {
        return;
      }
      this.setHybridPaymentFlowInformationProps({
        hybrid_method: "stripe",
        product_id: this.selectedProduct?.name || this.productDetail?.title,
        coins: this.selectedProduct?.amount || 0,
        price: this.selectedProduct?.price || this.productDetail?.price,
        currency: this.selectedProduct?.currency || "TRY",
        is_new_card: 0, // oldCheckout.vue'daki gibi
      });

      let product_id = this.$route.query.product_id;
      let product_type = this.$route.query.product_type
        ? parseInt(this.$route.query.product_type)
        : 0;
      const return_url = this.getRedirectionUrl();

      // Email bilgisini hazırla
      // App modunda: email key'i hiç gönderilmez
      // Web modunda: URL'den gelen user_email veya form'dan girilen email kullanılır
      const email = this.isWebPlatform
        ? this.userEmailFromUrl || this.userInfo.email
        : null;

      const paymentData = createStripePaymentData(
        product_type,
        product_id,
        this.selectedPaymentMethod.id,
        return_url
      );
      // Email varsa paymentData'ya ekle
      if (email) {
        paymentData.email = email;
      }
      // Language parametresini ekle
      paymentData.language = this.currentLocale;

      endpoints
        .create_stripe_payment(this.$axios, paymentData)
        .then(async (payWithMethodResponse) => {
          if (payWithMethodResponse.status == 200) {
            await this.confirmPaymentResponseHandler(
              payWithMethodResponse.data
            );
          } else {
            sessionStorage.removeItem("uuid_stripe_payment");
            this.cardError = this.$t("errors.general_error");
            if (
              payWithMethodResponse.data &&
              payWithMethodResponse.data.error
            ) {
              this.$toast.error(payWithMethodResponse.data.error.message);
            }
            this.loading = false;
          }
        });
    },
    async confirmPaymentResponseHandler(payWithMethodResponse) {
      if (payWithMethodResponse.error) {
        this.loading = false;
        sessionStorage.removeItem("uuid_stripe_payment");
        this.cardError = payWithMethodResponse.error.message;
      } else if (payWithMethodResponse.status == "succeeded") {
        sessionStorage.removeItem("uuid_stripe_payment");

        this.$store.dispatch("payment/setStripePaymentSucceeded", true);
        this.$store.dispatch("payment/setStripeActivePage", "completePayment");
        this.goToPaymentCompleted();
      } else if (
        payWithMethodResponse.status == "requires_action" ||
        payWithMethodResponse.status == "requires_confirmation"
      ) {
        await this.$stripe
          .handleNextAction({
            clientSecret: payWithMethodResponse.client_secret,
          })
          .then((handleNextActionResponse) => {
            if (handleNextActionResponse.error) {
              this.loading = false;
              console.error(handleNextActionResponse.error);
              this.cardError = handleNextActionResponse.error.message;
            } else {
              console.log(
                "handleNextActionResponse.paymentIntent",
                handleNextActionResponse.paymentIntent
              );
              // Başarılı ödeme durumunda success sayfasına git
              if (
                handleNextActionResponse.paymentIntent.status === "succeeded"
              ) {
                this.goToPaymentCompleted();
              }
            }
          })
          .catch((err) => {
            console.error(err);
            this.loading = false;
            this.cardError = this.$t("errors.general_error");
          });
      } else {
        this.loading = false;
      }
    },
    removeError() {
      this.cardError = null;
    },
    cardImgName: function (cardData) {
      if (cardData && cardData.card && cardData.card.brand) {
        if (this.supportedCardIcons.indexOf(cardData.card.brand) >= 0) {
          return cardData.card.brand;
        }
      }

      return "generic-card";
    },
    goCardPayment() {
      //this.$store.dispatch("payment/setStripeActivePage", "stripePayment");
      this.showCheckoutPage = false;
      this.showCreditCardPayment = true;
      this.showPaymentMethods = false;
    },
    selectStripePaymentMethod(paymentMethod) {
      this.savedPaymentTokenId = null;
      this.selectedToken = null;
      this.$store.dispatch("payment/setSelectedPaymentMethod", paymentMethod);
    },
    goToPaymentCompleted() {
      this.loading = false;
      this.paymentSuccess = true;
    },
    continueShopping() {
      // PaymentCompleted.vue'daki gibi hybrid API kontrolü
      if (check_hybridapi_method_exist("close")) {
        // for ios client
        this.$hybridapi("updatePageTitle", this.$t("labels.do_payment"));
        this.$hybridapi("updateWindowSize", "medium");
        // Store'u temizle
        this.$store.dispatch("payment/setStripePaymentSucceeded", false);
        this.$store.dispatch("payment/setSelectedProduct", null);
        this.$store.dispatch("payment/setSelectedPaymentMethod", null);
        // Close çağır
        this.$hybridapi("close", "close");
      } else {
        // Web senaryosu için mevcut redirect mantığı
        let [success_redirect_url, failed_redirect_url] = get_redirect_urls();

        // Eğer URL'de yoksa, sessionStorage'dan geri yükle (Safari için)
        if (!success_redirect_url && !failed_redirect_url) {
          try {
            const savedParams = sessionStorage.getItem(
              "stripe_redirect_params"
            );
            if (savedParams) {
              const parsedParams = JSON.parse(savedParams);
              success_redirect_url = parsedParams.success_redirect_url;
              failed_redirect_url = parsedParams.failed_redirect_url;
            }
          } catch (error) {
            console.error("sessionStorage error:", error);
          }
        }

        if (success_redirect_url) {
          try {
            const url_to_go = new URL(success_redirect_url);

            if (!["http:", "https:"].includes(url_to_go.protocol)) {
              throw new Error("Invalid protocol");
            }

            // sessionStorage'ı temizle
            sessionStorage.removeItem("stripe_redirect_params");

            window.location.replace(url_to_go);
          } catch (error) {
            console.error("invalid redirect url", error);
            // Fallback: ana sayfaya yönlendir
            this.$router.push({
              path: "/",
              query: this.$route.query,
            });
          }
        } else {
          // Fallback: ana sayfaya yönlendir
          this.$router.push({
            path: "/",
            query: this.$route.query,
          });
        }
      }
    },
    getRedirectionUrl() {
      var url = new URL(process.env.VUE_APP_URL);

      // Tüm mevcut query parametrelerini al ve sessionStorage'a kaydet
      const allParams = {
        ...this.$route.query,
        ...get_redirect_url_map(),
      };

      // Safari için kritik parametreleri sessionStorage'a kaydet
      try {
        const existing = sessionStorage.getItem("stripe_redirect_params");
        if (!existing) {
          sessionStorage.setItem(
            "stripe_redirect_params",
            JSON.stringify(allParams)
          );
        }
      } catch (error) {
        console.error("sessionStorage error:", error);
      }

      url.search = new URLSearchParams(allParams);
      url.pathname = "stripe/redirect";
      return url.toString();
    },
    async paypalPayButtonClicked() {
      if (
        this.isSaveCard === true &&
        check_hybridapi_method_exist("openSessionedBrowser") == true
      ) {
        const redirect_url =
          window.location.origin +
          window.location.pathname +
          "/paypal_card" +
          window.location.search;
        this.$hybridapi("openSessionedBrowser", redirect_url);
      } else {
        // old way
        let product_id = this.$route.query.product_id;
        let product_type = this.$route.query.product_type
          ? parseInt(this.$route.query.product_type)
          : 0;

        this.proceedWithPayPal(product_id, product_type, null, this.isSaveCard);
      }
    },
    async getSavedPaymentToken() {
      const provider = PaymentProvider.PAYPAL;
      endpoints
        .saved_cards(this.$axios, { provider, language: this.currentLocale })
        .then((savedCardResponse) => {
          if (savedCardResponse.status == 200) {
            const response_data = savedCardResponse.data;
            this.paypalPaymentTokens = [...response_data.data];

            if (this.paypalPaymentTokens.length > 0) {
              this.selectPaymentToken(this.paypalPaymentTokens[0]);
            }
          }
        })
        .catch((err) => {
          console.log(err);
          this.loading = false;
        });
    },
    async proceedWithPayPal(
      product_id,
      product_type,
      savedPaymentTokenId = null,
      isSavePaymentTokenPermission = false
    ) {
      this.loading = true;
      const paymentData = {
        idempotency_key: getPaymentUUID(),
        product_id: product_id,
        product_type: product_type,
        language: this.currentLocale,
        ...(savedPaymentTokenId != null
          ? { vault_id: savedPaymentTokenId }
          : { is_vault_store: isSavePaymentTokenPermission }),
        paypal_session_id: PayPal.getPayPalSessionId(),
        ...get_redirect_url_map(),
      };
      endpoints
        .paypal_payment(this.$axios, paymentData)
        .then((paymentPaypalResponse) => {
          const data = paymentPaypalResponse.data;
          generatePaymentUUID();

          if (paymentPaypalResponse.status == 200) {
            this.loading = false;
            window.location.href = data.redirect_url;
            return;
          } else {
            if (data.error) {
              this.$toast.error(data.error.message);
            } else {
              this.$toast.error(this.$t("errors.general_error"));
            }

            logger.debugError(
              "Unexpected PayPal exception caught",
              paymentPaypalResponse
            );
            this.hybridClose("close");

            this.loading = false;
          }
        });
    },
    handleNewCouponAdded() {
      location.reload();
    },
    selectPaymentToken(paymentToken) {
      if (paymentToken) {
        this.$store.dispatch("payment/setSelectedPaymentMethod", null);
        this.savedPaymentTokenId = paymentToken.id;
        this.selectedToken = paymentToken;
      } else {
        this.savedPaymentTokenId = null;
        this.selectedToken = paymentToken;
      }
    },
    onDocumentResize() {
      this.width = `${window.innerWidth * 0.6}px`;
    },
    openStripeRemove() {
      this.showRemove = true;
    },
    closeStripeRemove() {
      this.showRemove = false;
    },
    async getProductDetail(package_id, product_id = null) {
      try {
        this.productDetailLoading = true;

        const order_id = this.$route.query.order_id || this.orderId;

        // Locale'i doğrudan $i18n'den al, yoksa URL'den kontrol et
        let locale = this.$i18n.locale || "en";
        try {
          const urlParams = new URLSearchParams(window.location.search);
          const urlLocale = urlParams.get("locale");
          const supportedLocales = [
            "en",
            "tr",
            "es",
            "fr",
            "de",
            "it",
            "pt",
            "ru",
            "ja",
            "zh",
            "ar",
            "th",
          ];
          if (urlLocale && supportedLocales.includes(urlLocale)) {
            locale = urlLocale;
            // i18n locale'ini de güncelle
            if (this.$i18n.locale !== urlLocale) {
              this.$i18n.locale = urlLocale;
            }
          }
        } catch (e) {
          // ignore
        }

        // package_id varsa onu kullan, yoksa product_id kullan
        const requestData = package_id
          ? {
              package_id: package_id,
              language: locale,
              order_id: order_id,
            }
          : product_id
          ? {
              product_id: product_id,
              language: locale,
              order_id: order_id,
            }
          : null;

        if (!requestData) {
          console.error("Neither package_id nor product_id provided");
          this.productDetailLoading = false;
          return;
        }

        console.log("getProductDetail request data:", requestData);
        console.log("Current locale:", locale);
        console.log("$i18n.locale:", this.$i18n.locale);

        const response = await endpoints.get_product_detail(
          this.$axios,
          requestData
        );

        console.log("Full API Response:", response);

        if (response.data && response.data.details) {
          this.productDetail = response.data.details;

          this.flagLoading = true; // Reset flag loading when new product loads
          console.log("Product Detail loaded:", this.productDetail);
        } else {
          console.error("Product detail API error:", response.data);
        }
      } catch (error) {
        console.error("Product detail fetch error:", error);
        // Hata durumunda loading'i kapat
        this.loading = false;
        this.$hybridapi("hideLoading");
      } finally {
        this.productDetailLoading = false;
        // Payment success durumunda product detail yüklendiğinde loading'i kapat
        if (this.paymentSuccess) {
          this.loading = false;
          this.$hybridapi("hideLoading");
        }
      }
    },
    getCountryFlag(iconKey) {
      if (!iconKey) return "";

      // Güvenli CDN URL'leri whitelist
      const allowedCDNs = [
        "https://d36m0a2er0ydwe.cloudfront.net",
        process.env.NEXT_PUBLIC_CDN_URL,
      ].filter(Boolean); // undefined değerleri temizle

      // URL'den cdn_url parametresini al ve doğrula
      const urlParams = new URLSearchParams(window.location.search);
      const urlCdnUrl = urlParams.get("cdn_url");

      // URL'den gelen CDN URL'i whitelist'te varsa kullan, yoksa güvenli default kullan
      let cdnUrl;
      if (
        urlCdnUrl &&
        allowedCDNs.some((allowed) => urlCdnUrl.startsWith(allowed))
      ) {
        cdnUrl = urlCdnUrl;
      } else {
        cdnUrl =
          process.env.NEXT_PUBLIC_CDN_URL ||
          "https://d36m0a2er0ydwe.cloudfront.net";
      }

      // Path injection koruması - sadece alfanumerik karakterler ve alt çizgi, tire, nokta
      const sanitizedIconKey = iconKey
        .toLowerCase()
        .replace(/[^a-z0-9._-]/g, "");

      return `${cdnUrl}/public/images/flags/${sanitizedIconKey}.png`;
    },
    getAdditionalInfo(iconKey) {
      if (!this.productDetail?.additional_info) return null;
      return this.productDetail.additional_info.find(
        (info) => info.icon_key === iconKey
      );
    },
    hybridClose(exit_location = "close") {
      localStorage.removeItem("order_id");
      this.$hybridapi("close", exit_location);
    },
    applyDiscount() {
      if (this.discountCode && this.discountCode.trim()) {
        this.loading = true;

        endpoints
          .add_coupon(this.$axios, {
            coupon_code: this.discountCode,
            apply_immediately: !(this.orderId == null),
            order_id: this.orderId,
            language: this.currentLocale,
          })
          .then((response) => {
            if (response.status == 200) {
              if (response.data.success) {
                this.$toast.success(
                  this.$t("checkout.discount_code_applied_success")
                );
                this.discountCode = "";

                if (response.data.new_price_text && this.productDetail) {
                  this.productDetail.price_text = response.data.new_price_text;
                }
              }
            } else if (response.data.error) {
              this.$toast.error(response.data.error.message);
            } else {
              console.error(response.data);
            }
            this.loading = false;
          })
          .catch((err) => {
            console.error("Failed to apply discount code", err);
            this.$toast.error(this.$t("checkout.discount_code_failed"));
            this.loading = false;
          });
      }
    },
    toggleOrderSummary() {
      this.orderSummaryExpanded = !this.orderSummaryExpanded;
    },
    handlePaymentSuccess(paymentData) {
      // Ödeme başarılı - success panel'i göster
      this.paymentSuccess = true;
      this.paymentData = paymentData;
    },
    triggerStripePayment() {
      // SrCheckoutForm'daki form submit'ini tetikle
      const form = document.getElementById("payment-form");
      console.log("Form found:", form);
      if (form) {
        console.log("Dispatching submit event");
        form.dispatchEvent(new Event("submit"));
      } else {
        console.error("Payment form not found! Falling back to PayPal...");
        // Form bulunamazsa PayPal'a yönlendir
        this.paypalPayButtonClicked();
      }
    },
    checkPaymentSuccess() {
      // URL parametresini kontrol et
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get("payment_success") === "true") {
        this.paymentSuccess = true;
        this.paymentData = {
          paymentMethod: "Card",
          lastFour: urlParams.get("last_four") || "1234",
        };

        // Product detail yükle (URL'de yoksa sessionStorage'dan al)
        let package_id =
          this.$route.query.package_id || this.$route.params.package_id;
        let product_id = this.$route.query.product_id;

        // URL'de yoksa sessionStorage'dan al (Safari gizli sekme için)
        if (!package_id || !product_id) {
          try {
            const savedParams = sessionStorage.getItem(
              "stripe_redirect_params"
            );
            if (savedParams) {
              const parsed = JSON.parse(savedParams);
              if (!package_id && parsed && parsed.package_id) {
                package_id = parsed.package_id;
              }
              if (!product_id && parsed && parsed.product_id) {
                product_id = parsed.product_id;
              }
            }
          } catch (e) {
            // ignore
          }
        }

        if (package_id) {
          this.getProductDetail(package_id, null).catch((err) => {
            // Product detail yüklenemediğinde loading'i kapat
            this.loading = false;
            this.$hybridapi("hideLoading");
            console.error(
              "getProductDetail error in checkPaymentSuccess:",
              err
            );
          });
        } else if (product_id) {
          this.getProductDetail(null, product_id).catch((err) => {
            // Product detail yüklenemediğinde loading'i kapat
            this.loading = false;
            this.$hybridapi("hideLoading");
            console.error(
              "getProductDetail error in checkPaymentSuccess:",
              err
            );
          });
        }

        // URL'den parametreleri temizle
        urlParams.delete("payment_success");
        urlParams.delete("last_four");
        const newUrl =
          window.location.pathname +
          (urlParams.toString() ? "?" + urlParams.toString() : "");
        window.history.replaceState({}, "", newUrl);
      }
    },
    onFlagLoad() {
      this.flagLoading = false;
    },
    onFlagError() {
      this.flagLoading = false;
      console.warn("Flag image failed to load");
    },
    getBaseUrl() {
      // URL'den success_redirect_url parametresini al ve base URL'i çıkar
      const urlParams = new URLSearchParams(window.location.search);
      const successRedirectUrl = urlParams.get("success_redirect_url");

      if (successRedirectUrl) {
        try {
          const url = new URL(successRedirectUrl);
          // /reserve/success kısmından önceki base URL'i al
          const pathParts = url.pathname.split("/");
          const reserveIndex = pathParts.indexOf("reserve");
          if (reserveIndex > 0) {
            const basePath = pathParts.slice(0, reserveIndex).join("/");
            return `${url.protocol}//${url.host}${basePath}`;
          }
          return `${url.protocol}//${url.host}`;
        } catch (error) {
          console.error("Invalid success_redirect_url:", error);
        }
      }

      // Fallback: mevcut domain'i kullan
      return window.location.origin;
    },
    // Yeni kullanıcı bilgileri formu metodları
    validateUserInfo() {
      this.errors = {};
      let isValid = true;

      // Email validation (required)
      // Sadece web platformunda ve URL'den email gelmediğinde kontrol et
      // (App modunda form zaten gösterilmez, ama yine de kontrol edelim)
      if (this.isWebPlatform && !this.userEmailFromUrl) {
        if (!this.userInfo.email) {
          this.errors.email = this.$t("checkout.email_required_error");
          isValid = false;
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.userInfo.email)) {
          this.errors.email = this.$t("checkout.email_invalid_error");
          isValid = false;
        }
      }

      // First name and last name validation (optional but validate if filled)
      if (
        this.userInfo.firstName.trim() &&
        this.userInfo.firstName.trim().length < 2
      ) {
        this.errors.firstName = this.$t("checkout.first_name_min_length_error");
        isValid = false;
      }

      if (
        this.userInfo.lastName.trim() &&
        this.userInfo.lastName.trim().length < 2
      ) {
        this.errors.lastName = this.$t("checkout.last_name_min_length_error");
        isValid = false;
      }

      // Phone validation (optional but validate if filled)
      if (this.userInfo.phone.trim()) {
        // Phone number format check (only digits)
        const phoneRegex = /^[0-9]+$/;
        if (!phoneRegex.test(this.userInfo.phone.trim())) {
          this.errors.phone = this.$t("checkout.phone_invalid_error");
          isValid = false;
        }
      }

      return isValid;
    },
    clearError(field) {
      if (this.errors[field]) {
        delete this.errors[field];
      }
    },
    handlePhoneInput(event) {
      // Sadece rakamları al
      const value = event.target.value.replace(/[^0-9]/g, "");
      this.userInfo.phone = value;
      this.clearError("phone");
    },
    allowOnlyNumbers(event) {
      // Sadece rakam tuşlarına izin ver
      const charCode = event.which ? event.which : event.keyCode;
      if (charCode < 48 || charCode > 57) {
        event.preventDefault();
      }
    },
    async submitUserInfo() {
      // App platformunda bu metod çağrılmamalı, ama yine de kontrol edelim
      if (!this.isWebPlatform) {
        return;
      }

      if (!this.validateUserInfo()) {
        return;
      }

      this.loading = true;

      try {
        // URL'den user_email varsa API'ye gönderme, direkt Stripe formuna geç
        if (this.userEmailFromUrl) {
          this.showStripeForm = true;
          this.loading = false;
          return;
        }

        // API'ye kullanıcı bilgilerini gönder
        const response = await this.$axios.post(
          `${
            process.env.VUE_APP_API_URL_NEW ||
            "https://api.second-phone-test.com/api"
          }/esim/user/create_user_on_order`,
          {
            email: this.userInfo.email,
            name: this.userInfo.firstName || null,
            last_name: this.userInfo.lastName || null,
            phone_number: this.userInfo.phone || null,
            language: this.currentLocale,
          }
        );

        if (response.status === 200) {
          // Kullanıcı bilgileri başarıyla kaydedildi, şimdi payment_settings çağır
          let product_id = this.$route.query.product_id;
          let product_type = this.$route.query.product_type
            ? parseInt(this.$route.query.product_type)
            : 0;

          if (product_id && product_type) {
            // Payment settings'i çağır, sonra Stripe formuna geç
            await this.getPaymentMethods(product_id, product_type);
          }

          // Stripe formuna geç
          this.showStripeForm = true;
        } else {
          this.$toast.error(this.$t("checkout.user_info_save_error"));
        }
      } catch (error) {
        console.error("User info submission error:", error);
        this.$toast.error(this.$t("checkout.general_error_try_again"));
      } finally {
        this.loading = false;
      }
    },
    goBackToUserInfo() {
      this.showStripeForm = false;
    },
    saveSessionOnMount() {
      // Tüm mevcut query parametrelerini al
      const allParams = {
        ...this.$route.query,
        ...get_redirect_url_map(),
      };

      try {
        const existing = sessionStorage.getItem("stripe_redirect_params");
        if (!existing) {
          sessionStorage.setItem(
            "stripe_redirect_params",
            JSON.stringify(allParams)
          );
        }
      } catch (error) {
        console.error("sessionStorage error:", error);
      }
    },
  },
};
</script>
